/**
 * VerifiedBadge + TrustMarks — `Oprappo Design System.dc.html § 01 ভিত্তি`,
 * "আইকন-মাত্র নিয়ন্ত্রণের নাম ও আস্থার চিহ্ন", lower half.
 *
 * NOT on the docs/10 § WP-B component list, but specified in the design system file with
 * its own rule. Built here and reported.
 *
 * Frame rule: "ব্যাজটি কখনও একা বসবে না। ট্যাপ করলে একটি শিট খুলবে যেখানে লেখা থাকবে কী
 * যাচাই হয়েছে, কী হয়নি, আর কখন হয়েছে। যা যাচাই করা হয়নি তা লুকানো হবে না — অব্যাখ্যাত
 * আস্থার চিহ্ন আস্থা তৈরি করে না, দায় তৈরি করে।"
 *
 * The badge itself is the § 03 success badge (`border:1px solid #1E6B4A; padding:3px 8px;
 * 500 13px/1.75`) as drawn on the § 03 card. `VerifiedBadge` requires an `onExplain`
 * handler so the badge cannot ship without its explanation.
 *
 * TrustMarks panels: `flex:1; min-width:280px; border:1px solid #E2DED5; padding:12px 14px`.
 * Heading `600 13px/1.75; letter-spacing:.09em; text-transform:uppercase` — success
 * #1E6B4A for what is verified, amber-text #8A5A0C for what is not.
 * List `400 13px/1.85` ink2 — note 1.85, not the 1.75 body band; transcribed as drawn.
 */
import { Badge } from './Badge.js';
import { cx } from './internal/cx.js';

export interface VerifiedBadgeProps {
  /** Bangla badge word, as drawn: `যাচাইকৃত`. */
  labelBn: string;
  /** Required: the badge never sits alone — tapping it opens the explanation. */
  onExplain: () => void;
  className?: string | undefined;
}

export function VerifiedBadge({ labelBn, onExplain, className }: VerifiedBadgeProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onExplain}
      className={cx('bg-transparent border-0 p-0 flex-none', className)}
    >
      <Badge labelBn={labelBn} tone="success" />
    </button>
  );
}

export interface TrustMarksProps {
  /** Bangla heading of the "what is verified" panel, e.g. `যা যাচাই হয়`. */
  verifiedHeadingBn: string;
  /** Bangla list, e.g. `জাতীয় পরিচয়পত্র · মোবাইল নম্বর · ব্যবসার ঠিকানা · ট্রেড লাইসেন্স (থাকলে)`. */
  verifiedBn: string;
  /** Bangla heading of the "what is not verified" panel, e.g. `যা যাচাই হয় না`. */
  notVerifiedHeadingBn: string;
  /** Bangla list, e.g. `কাজের দক্ষতা · বিমা · সরঞ্জামের গুণমান · যন্ত্রাংশের দাম`. */
  notVerifiedBn: string;
  className?: string | undefined;
}

function Panel({
  headingBn,
  headingClass,
  bodyBn,
}: {
  headingBn: string;
  headingClass: string;
  bodyBn: string;
}): JSX.Element {
  return (
    <div className="flex-1 min-w-[280px] border border-border px-[14px] py-[12px]">
      <div
        className={cx(
          'font-anek font-semibold text-[13px] leading-[1.75] tracking-[.09em] uppercase mb-[9px]',
          headingClass,
        )}
      >
        {headingBn}
      </div>
      <div className="font-anek font-normal text-[13px] leading-[1.85] text-ink2">{bodyBn}</div>
    </div>
  );
}

export function TrustMarks({
  verifiedHeadingBn,
  verifiedBn,
  notVerifiedHeadingBn,
  notVerifiedBn,
  className,
}: TrustMarksProps): JSX.Element {
  return (
    <div className={cx('flex flex-wrap gap-[16px]', className)}>
      <Panel headingBn={verifiedHeadingBn} headingClass="text-success" bodyBn={verifiedBn} />
      <Panel headingBn={notVerifiedHeadingBn} headingClass="text-amber-text" bodyBn={notVerifiedBn} />
    </div>
  );
}
